import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState("intro");
  const [userName, setUserName] = useState("");
  const nameRef = useRef(null);

  useEffect(() => {
    const initSession = async () => {
      let sid = localStorage.getItem("lumis_session_id");
      if (!sid) {
        try {
          const res = await axios.post(`${API}/session/create`);
          sid = res.data.session_id;
          localStorage.setItem("lumis_session_id", sid);
        } catch (e) { console.error("Session create error:", e); }
      }
      if (localStorage.getItem("lumis_onboarding_complete") === "true") {
        navigate("/modules", { replace: true });
      }
    };
    initSession();
  }, [navigate]);

  useEffect(() => {
    if (step === "name") setTimeout(() => nameRef.current?.focus(), 80);
  }, [step]);

  const handleNameSubmit = async () => {
    const name = userName.trim();
    if (!name) return;
    localStorage.setItem("lumis_user_name", name);
    const sid = localStorage.getItem("lumis_session_id");
    try {
      await axios.put(`${API}/session/${sid}/profile`, { profile: { name }, priority_categories: [] });
    } catch (e) { console.error(e); }
    localStorage.setItem("lumis_onboarding_complete", "true");
    setStep("done");
    setTimeout(() => navigate("/modules", { replace: true }), 2000);
  };

  const onNameKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); handleNameSubmit(); }
  };

  // ── INTRO ──
  if (step === "intro") return (
    <div className="h-[100dvh] bg-[#EEF2FF] flex flex-col items-center justify-center p-6" data-testid="onboarding-intro">
      <div className="lumis-fade w-full max-w-sm text-center space-y-8">
        <div className="space-y-3">
          <div className="text-5xl text-[#1E3A8A] font-outfit font-light">✦</div>
          <h1 className="text-4xl font-outfit font-light text-gray-900 tracking-tight">Lumis</h1>
          <p className="text-gray-500 text-base font-manrope leading-relaxed">
            Um espaço seguro de autoconhecimento e transformação pessoal.
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-3xl p-6 text-left space-y-2">
          <p className="text-gray-700 text-sm font-manrope leading-relaxed">
            Aqui você encontra clareza, direção e caminhos concretos para o que está vivendo.
          </p>
          <p className="text-gray-500 text-xs font-manrope">Sem julgamentos. No seu tempo.</p>
        </div>
        <button
          data-testid="onboarding-start-btn"
          onClick={() => setStep("name")}
          className="w-full bg-[#1E3A8A] text-white rounded-full px-8 py-4 font-manrope font-medium hover:bg-[#172554] active:scale-[0.98] transition-all shadow-[0_4px_14px_rgba(30,58,138,0.3)]"
        >
          Começar
        </button>
      </div>
    </div>
  );

  // ── NOME ──
  if (step === "name") return (
    <div className="h-[100dvh] bg-[#EEF2FF] flex flex-col p-6" data-testid="onboarding-name">
      <div className="flex items-center justify-between mb-6">
        <div className="text-[#1E3A8A] font-outfit text-xl">✦</div>
        <span className="text-gray-500 text-xs font-manrope">Antes de começar</span>
      </div>
      <div className="flex-1" />
      <div className="space-y-6 pb-2">
        <div className="lumis-fade">
          <p className="text-gray-900 text-xl font-outfit font-light leading-relaxed mb-1">
            Como você se chama?
          </p>
          <p className="text-gray-500 text-sm font-manrope">
            Assim posso conversar com você de forma mais pessoal.
          </p>
        </div>
        <div className="space-y-3">
          <input
            ref={nameRef}
            data-testid="onboarding-name-input"
            type="text"
            value={userName}
            onChange={e => setUserName(e.target.value)}
            onKeyDown={onNameKey}
            placeholder="Seu primeiro nome..."
            className="w-full bg-white text-gray-900 border border-gray-300 rounded-2xl px-5 py-4 focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A] outline-none transition-all placeholder:text-gray-400 font-manrope text-base"
          />
          <button
            data-testid="onboarding-name-btn"
            onClick={handleNameSubmit}
            disabled={!userName.trim()}
            className="w-full bg-[#1E3A8A] text-white rounded-full py-4 font-manrope font-medium hover:bg-[#172554] active:scale-[0.98] transition-all disabled:opacity-30 shadow-[0_4px_14px_rgba(30,58,138,0.3)]"
          >
            Continuar
          </button>
        </div>
      </div>
    </div>
  );

  // ── CONCLUÍDO ──
  return (
    <div className="h-[100dvh] bg-[#EEF2FF] flex items-center justify-center" data-testid="onboarding-done">
      <div className="lumis-fade text-center space-y-5 px-6 max-w-sm">
        <div className="text-4xl text-[#1E3A8A] font-outfit">✦</div>
        <h2 className="text-2xl font-outfit font-light text-gray-900">
          Bem-vindo{userName ? `, ${userName}` : ""}.
        </h2>
        <p className="text-gray-500 text-base font-manrope leading-relaxed">
          Espero que aqui você encontre o caminho que está buscando.
        </p>
      </div>
    </div>
  );
}
